/**
 * Keşif transport — `/api/debate/crawl/*`.
 *
 * Follows `personaLibraryClient.ts`'s typed-error model rather than
 * `authClient.ts`'s `Error(string)`: this surface has status codes the UI must
 * distinguish and cannot recover from a stringified message. 503
 * `capacity.crawl_global` means "try later", 429 `capacity.crawl_subject`
 * means "stop one of your own jobs", 409 `crawl.job.terminal` means "make a
 * new job", and 422 means the policy itself is invalid. Four different screens
 * from four codes.
 *
 * Every response is validated through `crawlEventCodec`'s zod schemas. REST
 * payloads share the SSE shapes, so validating both through one schema set is
 * what keeps a backend field rename from producing `undefined` deep inside a
 * component instead of a decode warning at the boundary.
 */
import type {
  CrawlAsset,
  CrawlEvent,
  CrawlFrontierStats,
  CrawlHost,
  CrawlJob,
  CrawlPage,
  CrawlPolicy,
} from "../domain/kesif";
import {
  AssetSchema,
  CrawlPolicySchema,
  FrontierStatsSchema,
  HostSchema,
  JobSchema,
  PageSchema,
  decodeCrawlEvent,
} from "./crawlEventCodec";
import { withCsrfHeader } from "./csrf";
import { runSseStream, type SseReconnectPolicy } from "./sseStream";

const BASE = "/api/debate/crawl";

/** Typed API error — preserves HTTP status and the backend `code`. */
export class CrawlApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  /** From `Retry-After`, when the backend supplied one (429/503). */
  readonly retryAfterS: number | null;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: unknown,
    retryAfterS: number | null = null,
  ) {
    super(message);
    this.name = "CrawlApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.retryAfterS = retryAfterS;
  }

  /** Feature flag off, or the orchestrator is not serving this router. */
  get isUnavailable(): boolean {
    return this.status === 404 || this.status === 501;
  }

  /** Capacity, not correctness — the same request will work later. */
  get isCapacity(): boolean {
    return this.status === 429 || this.status === 503;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = withCsrfHeader({
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(init?.headers as Record<string, string> | undefined),
  });
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { ...init, headers, credentials: "include" });
  } catch (err) {
    // No status at all — distinguish "server unreachable" from "server said
    // no", because only one of them is worth retrying automatically.
    throw new CrawlApiError(
      0,
      "network",
      err instanceof Error ? err.message : "Sunucuya ulaşılamadı.",
    );
  }

  if (res.status === 204) return undefined as T;

  const raw = await res.text();
  let body: unknown = null;
  if (raw) {
    try {
      body = JSON.parse(raw);
    } catch {
      body = null;
    }
  }

  if (!res.ok) {
    const env = (body ?? {}) as {
      code?: string;
      message?: string;
      detail?: string;
      details?: unknown;
    };
    const retryAfter = Number(res.headers.get("Retry-After"));
    throw new CrawlApiError(
      res.status,
      env.code ?? `http_${res.status}`,
      env.message ?? env.detail ?? `İstek başarısız (HTTP ${res.status}).`,
      env.details,
      Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : null,
    );
  }
  return body as T;
}

/**
 * Decode a list of wire rows, dropping the ones that fail validation.
 *
 * Dropping beats throwing here: one malformed row out of 200 pages should
 * cost that row, not the whole table. The warning names the endpoint so the
 * cause is findable rather than mysterious.
 */
function decodeList<T>(
  schema: { safeParse(v: unknown): { success: boolean; data?: unknown } },
  rows: unknown,
  where: string,
): T[] {
  if (!Array.isArray(rows)) return [];
  const out: T[] = [];
  let dropped = 0;
  for (const row of rows) {
    const parsed = schema.safeParse(row);
    if (parsed.success) out.push(parsed.data as T);
    else dropped += 1;
  }
  if (dropped > 0 && typeof console !== "undefined") {
    console.warn(`[crawlClient] ${where}: ${dropped} satır şema dışı, atlandı`);
  }
  return out;
}

function decodeOne<T>(
  schema: { safeParse(v: unknown): { success: boolean; data?: unknown } },
  row: unknown,
  where: string,
): T {
  const parsed = schema.safeParse(row);
  if (!parsed.success) {
    throw new CrawlApiError(0, "decode", `${where} yanıtı beklenen şemaya uymuyor.`);
  }
  return parsed.data as T;
}

/* ---------- job lifecycle ---------- */

export interface CreateJobInput {
  name: string;
  seedUrls: string[];
  policy: CrawlPolicy;
  autoStart: boolean;
}

export interface CreateJobResult {
  job: CrawlJob;
  started: boolean;
  streamPath: string;
}

export async function createCrawlJob(input: CreateJobInput): Promise<CreateJobResult> {
  const body = await request<{
    job: unknown;
    started?: boolean;
    streamPath?: string;
  }>("/jobs", { method: "POST", body: JSON.stringify(input) });
  return {
    job: decodeOne<CrawlJob>(JobSchema, body.job, "İş oluşturma"),
    started: Boolean(body.started),
    streamPath: String(body.streamPath ?? ""),
  };
}

export interface JobListResult {
  jobs: CrawlJob[];
  total: number;
}

export async function listCrawlJobs(opts?: {
  limit?: number;
  offset?: number;
  status?: string;
}): Promise<JobListResult> {
  const q = new URLSearchParams();
  if (opts?.limit) q.set("limit", String(opts.limit));
  if (opts?.offset) q.set("offset", String(opts.offset));
  if (opts?.status) q.set("status", opts.status);
  const suffix = q.toString() ? `?${q}` : "";
  const body = await request<{ jobs: unknown; total?: number }>(`/jobs${suffix}`);
  return {
    jobs: decodeList<CrawlJob>(JobSchema, body.jobs, "GET /jobs"),
    total: Number(body.total ?? 0),
  };
}

export interface JobDetail {
  job: CrawlJob;
  hosts: CrawlHost[];
  frontier: CrawlFrontierStats;
  live: boolean;
}

export async function getCrawlJob(jobId: string): Promise<JobDetail> {
  const body = await request<{
    job: unknown;
    hosts: unknown;
    frontier: unknown;
    live?: boolean;
  }>(`/jobs/${jobId}`);
  return {
    job: decodeOne<CrawlJob>(JobSchema, body.job, "İş detayı"),
    hosts: decodeList<CrawlHost>(HostSchema, body.hosts, `GET /jobs/${jobId}`),
    frontier: decodeOne<CrawlFrontierStats>(
      FrontierStatsSchema,
      body.frontier ?? {},
      "Frontier",
    ),
    live: Boolean(body.live),
  };
}

/** Only budget knobs are patchable; scope/robots/render are immutable mid-crawl. */
export interface PolicyPatch {
  maxPages?: number;
  maxDepth?: number;
  requestsPerSecond?: number;
  maxDurationMinutes?: number;
  adaptiveStop?: boolean;
}

export async function patchCrawlPolicy(
  jobId: string,
  patch: PolicyPatch,
): Promise<CrawlPolicy> {
  const body = await request<{ policy: unknown }>(`/jobs/${jobId}/policy`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  // Validated rather than trusted: the backend CLAMPS budgets to server
  // ceilings, so the response is authoritative and may differ from what was
  // sent. Decoding it is what keeps the form showing the real values.
  return decodeOne<CrawlPolicy>(CrawlPolicySchema, body.policy, "Politika");
}

export interface LifecycleResult {
  status: string;
  streamPath: string;
  note: string;
}

async function lifecycle(jobId: string, action: string): Promise<LifecycleResult> {
  const body = await request<{ status?: string; streamPath?: string; note?: string }>(
    `/jobs/${jobId}/${action}`,
    { method: "POST" },
  );
  return {
    status: String(body.status ?? ""),
    streamPath: String(body.streamPath ?? ""),
    note: String(body.note ?? ""),
  };
}

export const startCrawlJob = (jobId: string) => lifecycle(jobId, "start");
export const pauseCrawlJob = (jobId: string) => lifecycle(jobId, "pause");
export const resumeCrawlJob = (jobId: string) => lifecycle(jobId, "resume");
export const cancelCrawlJob = (jobId: string) => lifecycle(jobId, "cancel");

export async function deleteCrawlJob(jobId: string): Promise<void> {
  await request<unknown>(`/jobs/${jobId}`, { method: "DELETE" });
}

export interface ExtendResult extends LifecycleResult {
  policy: CrawlPolicy;
  /** Frontier rows the continued run will consume — what makes it worthwhile. */
  resumable: number;
}

/**
 * Raise a finished job's budget and continue it from its frontier.
 *
 * The cheap half of "re-crawl with a bigger budget": nothing already stored is
 * re-fetched, because `maxPages` stopped the FETCHING and left everything the
 * crawl discovered afterwards queued as `pending`. Fails with
 * `crawl.frontier.empty` when there is nothing left to consume — the caller
 * should then clone instead.
 */
export async function extendCrawlJob(
  jobId: string,
  patch: PolicyPatch & { maxPages: number },
): Promise<ExtendResult> {
  const body = await request<{
    status?: string;
    streamPath?: string;
    note?: string;
    policy: unknown;
    resumable?: number;
  }>(`/jobs/${jobId}/extend`, { method: "POST", body: JSON.stringify(patch) });
  return {
    status: String(body.status ?? ""),
    streamPath: String(body.streamPath ?? ""),
    note: String(body.note ?? ""),
    // Authoritative: the server clamps to its own ceiling, so the form must
    // show what was actually stored rather than what was requested.
    policy: decodeOne<CrawlPolicy>(CrawlPolicySchema, body.policy, "Politika"),
    resumable: Number(body.resumable ?? 0),
  };
}

/* ---------- collected data ---------- */

export interface PageListResult {
  pages: CrawlPage[];
  total: number;
}

export async function listCrawlPages(
  jobId: string,
  opts?: { limit?: number; offset?: number; pageType?: string; host?: string; q?: string },
): Promise<PageListResult> {
  const q = new URLSearchParams();
  if (opts?.limit) q.set("limit", String(opts.limit));
  if (opts?.offset) q.set("offset", String(opts.offset));
  if (opts?.pageType) q.set("page_type", opts.pageType);
  if (opts?.host) q.set("host", opts.host);
  if (opts?.q) q.set("q", opts.q);
  const suffix = q.toString() ? `?${q}` : "";
  const body = await request<{ pages: unknown; total?: number }>(
    `/jobs/${jobId}/pages${suffix}`,
  );
  return {
    pages: decodeList<CrawlPage>(PageSchema, body.pages, `GET /jobs/${jobId}/pages`),
    total: Number(body.total ?? 0),
  };
}

export async function getCrawlPage(pageId: string): Promise<CrawlPage> {
  const body = await request<{ page: unknown }>(`/pages/${pageId}`);
  return decodeOne<CrawlPage>(PageSchema, body.page, "Sayfa detayı");
}

export interface AssetListResult {
  assets: CrawlAsset[];
  total: number;
}

export async function listCrawlAssets(
  jobId: string,
  opts?: { limit?: number; offset?: number; kind?: string },
): Promise<AssetListResult> {
  const q = new URLSearchParams();
  if (opts?.limit) q.set("limit", String(opts.limit));
  if (opts?.offset) q.set("offset", String(opts.offset));
  if (opts?.kind) q.set("kind", opts.kind);
  const suffix = q.toString() ? `?${q}` : "";
  const body = await request<{ assets: unknown; total?: number }>(
    `/jobs/${jobId}/assets${suffix}`,
  );
  return {
    assets: decodeList<CrawlAsset>(AssetSchema, body.assets, `GET /jobs/${jobId}/assets`),
    total: Number(body.total ?? 0),
  };
}

export async function listCrawlHosts(jobId: string): Promise<CrawlHost[]> {
  const body = await request<{ hosts: unknown }>(`/jobs/${jobId}/hosts`);
  return decodeList<CrawlHost>(HostSchema, body.hosts, `GET /jobs/${jobId}/hosts`);
}

/* ---------- preflight ---------- */

export interface PreflightResult {
  ok: boolean;
  url: string;
  host: string;
  urlHash: string;
  code: string;
  message: string;
}

/**
 * Cheap SSRF/shape probe for one seed. Does NOT fetch the page, so it is safe
 * to call on every debounced keystroke in the wizard.
 *
 * Never throws for a rejected URL — an unreachable seed is normal user input,
 * not an exception. Only transport failure raises.
 */
export async function preflightCrawlUrl(url: string): Promise<PreflightResult> {
  const body = await request<Partial<PreflightResult>>(
    `/preflight?url=${encodeURIComponent(url)}`,
  );
  return {
    ok: Boolean(body.ok),
    url: String(body.url ?? url),
    host: String(body.host ?? ""),
    urlHash: String(body.urlHash ?? ""),
    code: String(body.code ?? ""),
    message: String(body.message ?? ""),
  };
}

/* ---------- live stream ---------- */

export interface CrawlStreamOptions {
  jobId: string;
  signal: AbortSignal;
  onEvent: (evt: CrawlEvent) => void;
  onError?: (err: Error) => void;
  /** 409 `crawl.job.not_live` — no engine in this process. Expected, not an error. */
  onNotLive?: () => void;
  onReconnecting?: (attempt: number, maxAttempts: number, delayMs: number) => void;
  reconnect?: SseReconnectPolicy;
}

/**
 * Attach a viewer to a live crawl.
 *
 * Backfill contract: this delivers only events emitted from the moment of
 * attachment. Callers hydrate history from `getCrawlJob` + `listCrawlPages`
 * (which work for any status) and then open this for continuation — the same
 * split the debate reattach uses, and what lets a 5.000-page job attach
 * instantly instead of replaying.
 */
export async function openCrawlStream(opts: CrawlStreamOptions): Promise<void> {
  await runSseStream<CrawlEvent>({
    url: `${BASE}/jobs/${opts.jobId}/stream`,
    method: "GET",
    signal: opts.signal,
    decode: decodeCrawlEvent,
    onEvent: opts.onEvent,
    ...(opts.onError ? { onError: opts.onError } : {}),
    ...(opts.onReconnecting ? { onReconnecting: opts.onReconnecting } : {}),
    ...(opts.reconnect ? { reconnect: opts.reconnect } : {}),
    onResponse: async (res) => {
      // The engine finished (or was never running in this process) between the
      // UI deciding to attach and the request landing. A race, not a fault:
      // the caller re-reads the terminal job state over REST.
      if (res.status === 409) {
        opts.onNotLive?.();
        return { kind: "stop" };
      }
      return null;
    },
  });
}
